import { CheckSquareBroken } from '@untitledui/icons'
import { useContext, useState } from 'react'
import { twJoin } from 'tailwind-merge'

import Button from '@components/designSystem/Button'
import Dialog from '@components/designSystem/Dialog/Dialog'
import useTypedTranslation from '@hooks/useTypedTranslation'
import { STATE_ICONS } from '@utils/icons.ts'

import { type BaseDialogProps, DialogContext } from './DialogContext'

interface ConfirmDialogProps extends BaseDialogProps {
  title?: string
  message: string
  confirmText?: string
  cancelText?: string
  onConfirm: () => void | Promise<void>
}

export function ConfirmDialog({
  title,
  message,
  confirmText,
  cancelText,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {

  const dialogContext = useContext(DialogContext)
  const { t } = useTypedTranslation()
  const [isLoading, setLoading] = useState(false)

  if (!dialogContext) {
    throw new Error('ConfirmDialog must be used within a DialogProvider')
  }

  const { setPreventBackdropClose } = dialogContext

  function handleClose() {
    if (isLoading) return
    onClose?.()
  }

  async function handleConfirm() {
    setLoading(true)
    setPreventBackdropClose(true)
    try {
      await onConfirm()
      onClose?.()
    } finally {
      setLoading(false)
      setPreventBackdropClose(false)
    }
  }

  return (
    <Dialog
      headerIcon={CheckSquareBroken}
      title={title ?? t('common.dialog.confirm.title')}
      description={message}
      size="sm"
      onClose={isLoading ? undefined : onClose} // Prevent closing when loading
      showCloseButton={!isLoading} // Hide close button when loading
      footer={
        <>
          <Button
            onClick={handleClose}
            isDisabled={isLoading}
            variant='outline'
          >
            {cancelText ?? t('common.dialog.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            isDisabled={isLoading}
            isLoading={isLoading}
            variant='filled'
          >
            {confirmText ?? t('common.dialog.confirm.action')}
          </Button>
        </>
      }
    />
  )
}

interface AlertDialogProps extends BaseDialogProps {
  title?: string
  message: string
  buttonText?: string
  variant?: 'info' | 'success' | 'warning' | 'error'
}

const styles = {
  common: {
    icon: 'size-6 grow-0 shrink-0',
    iconContainer: 'border-2',
  },
  variants: {
    info: {
      icon: 'text-bg-brand-solid font-bold',
      iconContainer: 'border-bg-brand-solid',
    },
    success: {
      icon: 'text-bg-success-solid',
      iconContainer: 'border-bg-success-solid',
    },
    warning: {
      icon: 'text-bg-warning-solid',
      iconContainer: 'border-bg-warning-solid',
    },
    error: {
      icon: 'text-bg-error-solid',
      iconContainer: 'border-bg-error-solid',
    },
  },
}

export function AlertDialog({
  title,
  message,
  buttonText = 'OK',
  onClose,
  variant = 'info',
}: AlertDialogProps) {
  const Icon = STATE_ICONS[variant]

  const variantStyles = styles.variants[variant]

  return (
    <Dialog
      headerIcon={<Icon className={twJoin(styles.common.icon, variantStyles.icon)} />}
      headerIconContainerClassName={twJoin(styles.common.iconContainer, variantStyles.iconContainer)}
      title={title}
      description={message}
      size="sm"
      onClose={onClose}
      footer={
        <Button variant='outline' onClick={onClose}>
          {buttonText}
        </Button>
      }
    />
  )
}